import { ArrowDown, ArrowUp, CheckCircle2, ShieldCheck, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { PlanSourceQuantities } from "../../shared/production/PlanSourceQuantities";
import type {
  WeeklyPlanRow,
  WeeklyPlanningLine,
} from "../../../shared/weekly-planning";

type Props = {
  row: WeeklyPlanRow;
  lines: WeeklyPlanningLine[];
  inputDisabled: boolean;
  busy: boolean;
  dirty: boolean;
  verifying: boolean;
  operatorSelected: boolean;
  index: number;
  rowCount: number;
  onUpdate: (rowId: string, field: string, value: string) => void;
  onMove: (rowId: string, direction: -1 | 1) => void;
  onRemove: (rowId: string) => void;
  onVerifyWorkOrder: (rowId: string, identity: {
    salesOrderNo: string | null;
    customerCode: string | null;
    correctionReason: string | null;
  }) => void;
};

const fieldId = (rowId: string, field: string) =>
  `weekly-plan-${rowId}-${field}`;

export function WeeklyPlanningRowEditor({
  row,
  lines,
  inputDisabled,
  busy,
  dirty,
  verifying,
  operatorSelected,
  index,
  rowCount,
  onUpdate,
  onMove,
  onRemove,
  onVerifyWorkOrder,
}: Props) {
  const verification = row.source?.workOrderVerification;
  const correction = verification?.correction;
  const mikro = verification?.mikroIdentity;
  const canVerify = row.kind === "PRODUCTION" && Boolean(row.source && row.workOrderNo) && operatorSelected;
  const [salesOrderNo, setSalesOrderNo] = useState(mikro?.salesOrderNo ?? row.source?.businessIdentity?.salesOrderNo ?? "");
  const [customerCode, setCustomerCode] = useState(mikro?.customerCode ?? row.source?.businessIdentity?.customerCode ?? "");
  const [correctionReason, setCorrectionReason] = useState(correction?.correctionReason ?? "");
  useEffect(() => {
    setSalesOrderNo(mikro?.salesOrderNo ?? row.source?.businessIdentity?.salesOrderNo ?? "");
    setCustomerCode(mikro?.customerCode ?? row.source?.businessIdentity?.customerCode ?? "");
    setCorrectionReason(correction?.correctionReason ?? "");
  }, [correction?.correctionReason, mikro?.customerCode, mikro?.salesOrderNo, row.id, row.source?.businessIdentity?.customerCode, row.source?.businessIdentity?.salesOrderNo]);
  return (
    <div className="weekly-planning-row-editor">
      <div className="weekly-planning-row-primary">
        <label>
          Hat
          <select
            value={row.lineId ?? ""}
            disabled={inputDisabled}
            onChange={(event) => onUpdate(row.id, "lineId", event.target.value)}
          >
            <option value="">Hat seçin</option>
            {lines
              .filter((line) => line.active)
              .map((line) => (
                <option key={line.id} value={line.id}>
                  {line.name}
                </option>
              ))}
          </select>
        </label>
        <label>
          İş türü
          <select
            value={row.kind}
            disabled={inputDisabled}
            onChange={(event) => onUpdate(row.id, "kind", event.target.value)}
          >
            <option value="PRODUCTION">Üretim</option>
            <option value="ACTIVITY">Etkinlik</option>
          </select>
        </label>
        <label>
          {row.kind === "PRODUCTION" ? "Ürün" : "Etkinlik"}
          <input
            value={row.productName}
            disabled={inputDisabled}
            onChange={(event) =>
              onUpdate(row.id, "productName", event.target.value)
            }
          />
        </label>
        {row.kind === "PRODUCTION" ? (
          <>
            <label>
              Ürün kodu
              <input
                value={row.productCode ?? ""}
                disabled={inputDisabled}
                onChange={(event) =>
                  onUpdate(row.id, "productCode", event.target.value)
                }
              />
            </label>
            <label>
              İş emri
              <input
                value={row.workOrderNo ?? ""}
                disabled={inputDisabled}
                onChange={(event) =>
                  onUpdate(row.id, "workOrderNo", event.target.value)
                }
              />
            </label>
          </>
        ) : null}
        <label className="weekly-planning-editor-note">
          İş talimatı
          <input
            value={row.note ?? ""}
            disabled={inputDisabled}
            onChange={(event) => onUpdate(row.id, "note", event.target.value)}
          />
        </label>
      </div>
      <div className="weekly-planning-quantity-inputs">
        {row.kind === "PRODUCTION" ? (
          <>
            <label>
              {row.source?.quantities ? "Planlanacak kalan miktar" : "Plan miktarı"}
              <input
                id={fieldId(row.id, "quantity")}
                readOnly={Boolean(row.carryoverFromRowId)}
                type="number"
                min="0"
                step="0.001"
                value={row.quantity ?? ""}
                disabled={inputDisabled}
                onChange={(event) =>
                  onUpdate(row.id, "quantity", event.target.value)
                }
              />
            </label>
            <label>
              Birim
              <select
                id={fieldId(row.id, "unit")}
                value={row.unit ?? ""}
                disabled={inputDisabled}
                onChange={(event) =>
                  onUpdate(row.id, "unit", event.target.value)
                }
              >
                <option value="">Seçin</option>
                <option value="kg">kg</option>
                <option value="koli">koli</option>
                <option value="adet">adet</option>
              </select>
            </label>
            <label>
              Saatlik kapasite
              <input
                id={fieldId(row.id, "capacity")}
                type="number"
                min="0"
                step="0.001"
                value={row.capacityPerHour ?? ""}
                disabled={inputDisabled}
                onChange={(event) =>
                  onUpdate(row.id, "capacityPerHour", event.target.value)
                }
              />
            </label>
          </>
        ) : (
          <label>
            Süre (saat)
            <input
              type="number"
              min="0"
              step="0.25"
              value={row.durationHours ?? ""}
              disabled={inputDisabled}
              onChange={(event) =>
                onUpdate(row.id, "durationHours", event.target.value)
              }
            />
          </label>
        )}
      </div>
      {row.source?.quantities ? <PlanSourceQuantities source={row.source.quantities} /> : null}
      {row.source ? (
        <section className="weekly-planning-identity" aria-label="Satır kimliği doğrulaması">
          <div>
            <small>EXCEL KAYNAĞI</small>
            <strong>{row.source.originalWorkOrderNo ?? "İş emri yok"}</strong>
            <span>{row.source.businessIdentity?.salesOrderNo ?? "Sipariş yok"} · {row.source.businessIdentity?.customerCode ?? "Müşteri kodu yok"}</span>
            <span>{row.source.businessIdentity?.customerName ?? "Müşteri adı yok"} · {row.source.businessIdentity?.country ?? "Ülke yok"}</span>
          </div>
          <div>
            <small>GÜNCEL TASLAK</small>
            <strong>{row.workOrderNo ?? "İş emri bekliyor"}</strong>
            <span>{row.productCode ?? "Ürün kodu yok"} · {row.quantity ?? "Miktar yok"} {row.unit ?? ""}</span>
            <span>Excel satır {row.source.sourceRow} · sıra {row.sequence}</span>
          </div>
          <div className={correction ? "is-verified" : "is-pending"}>
            <small>MIKRO DOĞRULAMASI</small>
            <strong>{correction ? <><CheckCircle2 size={15} /> Doğrulandı</> : "Başlatılamaz"}</strong>
            {correction && mikro ? (
              <>
                <span>{mikro.salesOrderNo ?? "Sipariş yok"} · {mikro.customerCode ?? "Müşteri yok"}</span>
                <span>İş emri toplamı {mikro.fullWorkOrderQuantity ?? "bilinmiyor"} {mikro.unit ?? ""}</span>
              </>
            ) : (
              <span>Doğrulanana kadar raporda kalır; üretim kuyruğuna aktarılmaz.</span>
            )}
          </div>
          {canVerify ? (
            <div className="weekly-planning-verify-action">
              <label>
                Mikro sipariş
                <input value={salesOrderNo} disabled={busy} onChange={(event) => setSalesOrderNo(event.target.value)} />
              </label>
              <label>
                Mikro müşteri kodu
                <input value={customerCode} disabled={busy} onChange={(event) => setCustomerCode(event.target.value)} />
              </label>
              <label>
                Düzeltme nedeni
                <input value={correctionReason} disabled={busy} placeholder="Kaynak ve Mikro farkının nedeni" onChange={(event) => setCorrectionReason(event.target.value)} />
              </label>
              <button
                type="button"
                disabled={busy || dirty}
                onClick={() => onVerifyWorkOrder(row.id, {
                  salesOrderNo: salesOrderNo.trim() || null,
                  customerCode: customerCode.trim() || null,
                  correctionReason: correctionReason.trim() || null,
                })}
              >
                <ShieldCheck size={16} />
                {verifying ? "Mikro doğrulanıyor…" : correction ? "Mikro'da yeniden doğrula" : "Mikro'da doğrula"}
              </button>
              {dirty ? <span>Önce taslağı kaydedin.</span> : null}
            </div>
          ) : null}
        </section>
      ) : null}
      <div className="weekly-planning-row-detail-actions">
        <button
          type="button"
          disabled={busy || index === 0}
          onClick={() => onMove(row.id, -1)}
        >
          <ArrowUp size={16} />
          Yukarı
        </button>
        <button
          type="button"
          disabled={busy || index === rowCount - 1}
          onClick={() => onMove(row.id, 1)}
        >
          <ArrowDown size={16} />
          Aşağı
        </button>
        <button type="button" disabled={busy} onClick={() => onRemove(row.id)}>
          <Trash2 size={16} />
          Sil
        </button>
      </div>
    </div>
  );
}
